import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import api from "../api";
import ProctorGate from "./ProctorGate";
import FaceRecheckGate from "./FaceRecheckGate";
import SecurityHardStop from "./SecurityHardStop";

/** Wraps a Sales Cert section (MCQ / Sales Pitch / Role Play) so proctoring
 *  fires ONLY when the candidate actually starts a section, not when they
 *  browse the hub.
 *
 *  Behaviour:
 *  - On mount, POST /participant/salescert/proctor/init to fetch the
 *    assignment shell + latest verify state.
 *  - If NOT id_verified → render full <ProctorGate /> (webcam + ID upload).
 *  - If id_verified AND we haven't yet rechecked in this browser session →
 *    render lightweight <FaceRecheckGate /> (webcam-only, 5-sec face check).
 *  - Once cleared, render the wrapped children without a page navigation.
 */
export default function RequireProctor({ children, backHref = "/participant/salescert", label = "Sales Cert" }) {
  const nav = useNavigate();
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState("loading"); // loading | full_gate | face_recheck | hard_stop | cleared
  const [err, setErr] = useState("");
  const [hold, setHold] = useState({ id: null, reason: null });

  useEffect(() => {
    api.post("/participant/salescert/proctor/init")
      .then((r) => {
        setSession(r.data);
        setPhase(r.data.id_verified ? "face_recheck" : "full_gate");
      })
      .catch((e) => setErr(e?.response?.data?.detail || String(e)));
  }, []);

  if (err) {
    return (
      <div className="max-w-2xl mx-auto p-8" data-testid="require-proctor-error">
        <h2 className="font-heading text-2xl font-bold mb-2">Could not load</h2>
        <p className="text-sm text-[#525252]">{err}</p>
      </div>
    );
  }
  if (phase === "loading" || !session) return <div className="p-10 text-sm text-[#878787]">Loading…</div>;

  if (phase === "full_gate") {
    return (
      <div className="max-w-3xl mx-auto p-4 fade-in" data-testid="section-proctor-gate">
        <button
          onClick={() => nav(backHref)}
          className="text-xs uppercase tracking-widest text-[#878787] hover:text-[#0A0A0A] flex items-center gap-1 mb-6"
        >
          <ChevronLeft size={14} /> Back
        </button>
        <div className="mb-6">
          <div className="overline mb-2">VI SALES CERTIFICATION 2026 · PROCTORING</div>
          <h1 className="font-heading font-black text-3xl tracking-tighter">Identity verification &amp; environment check</h1>
          <p className="text-sm text-[#525252] mt-2 max-w-xl leading-relaxed">
            Before starting the {label} section we need to verify your identity and confirm your webcam + mic are working.
            You&apos;ll only be asked to upload your VI Employee ID once for the whole certification attempt.
          </p>
        </div>
        <ProctorGate
          assignmentId={session.assignment_id}
          onReady={() => setPhase("face_recheck")}
        />
      </div>
    );
  }

  if (phase === "hard_stop") {
    return (
      <SecurityHardStop
        holdId={hold.id}
        reason={hold.reason}
        onResolvedResume={() => window.location.reload()}
        onResolvedReset={() => nav(backHref)}
      />
    );
  }

  if (phase === "face_recheck") {
    return (
      <div className="max-w-3xl mx-auto p-4 fade-in" data-testid="section-face-recheck-gate">
        <button
          onClick={() => nav(backHref)}
          className="text-xs uppercase tracking-widest text-[#878787] hover:text-[#0A0A0A] flex items-center gap-1 mb-6"
        >
          <ChevronLeft size={14} /> Back
        </button>
        <FaceRecheckGate
          assignmentId={session.assignment_id}
          section={label}
          onReady={() => setPhase("cleared")}
          onFullFallback={() => setPhase("full_gate")}
          onHardStop={({ holdId, reason }) => {
            setHold({ id: holdId, reason });
            setPhase("hard_stop");
          }}
        />
      </div>
    );
  }

  return typeof children === "function" ? children(session) : children;
}
